import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  BarChart3, Users, UserCheck, Clock, TrendingUp, Calendar,
  Wifi,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import apiClient from "../../utils/apiClient";
import {
  PageHeader, AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  StatsCard,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./attendance.scss";

const COLORS = ["#22c55e", "#f59e0b", "#ef4444", "#6b7280", "#3b82f6"];

/**
 * AttendanceAnalyticsPage — Visual analytics dashboard for attendance.
 * Route: /attendance/analytics
 */
const AttendanceAnalyticsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Filters
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({ branchId: "", startDate: "", endDate: "" });

  useEffect(() => {
    apiClient.get("/branches").then((res) => setBranches(res.data.branches || [])).catch(() => {});
  }, []);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.branchId) params.set("branchId", filters.branchId);
      if (filters.startDate) params.set("startDate", filters.startDate);
      if (filters.endDate) params.set("endDate", filters.endDate);

      const { data: d } = await apiClient.get(`/attendance/dashboard?${params.toString()}`);
      setData(d);
    } catch {
      setToast({ show: true, message: "Failed to load analytics.", type: "danger" });
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="attendance-page sl-page">
        <div className="text-center py-5"><Spinner animation="border" /></div>
      </div>
    );
  }

  const overview = data?.overview || {};
  const dailyTrend = data?.dailyTrend || [];
  const batchWise = data?.batchWise || [];
  const statusBreakdown = data?.statusBreakdown || [];
  const sourceBreakdown = data?.sourceBreakdown || [];

  // Transform for charts
  const dailyData = dailyTrend.map((d) => ({
    date: d._id,
    sessions: d.sessions,
    present: d.totalPresent,
    total: d.totalStudents,
    rate: d.totalStudents > 0 ? Math.round((d.totalPresent / d.totalStudents) * 100) : 0,
  }));

  const statusData = statusBreakdown.map((s) => ({
    name: s._id || "Unknown",
    value: s.count,
  }));

  const sourceData = sourceBreakdown.map((s) => ({
    name: s._id === "online" ? "Online" : "Manual/Offline",
    value: s.count,
  }));

  return (
    <div className="attendance-page sl-page">
      <PageHeader
        backButton
        onBack={() => navigate("/attendance")}
        icon={BarChart3}
        title="Attendance Analytics"
        description="Visual insights into attendance patterns across batches and branches"
      />

      {/* Filters */}
      <AppCard className="attendance-filters-card mb-3">
        <AppCardContent>
          <div className="attendance-filters">
            <div className="attendance-filter-item">
              <label>Branch</label>
              <select
                className="form-select form-select-sm"
                value={filters.branchId}
                onChange={(e) => handleFilterChange("branchId", e.target.value)}
              >
                <option value="">All Branches</option>
                {branches.map((b) => (
                  <option key={b._id} value={b._id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div className="attendance-filter-item">
              <label>From</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.startDate}
                onChange={(e) => handleFilterChange("startDate", e.target.value)}
              />
            </div>
            <div className="attendance-filter-item">
              <label>To</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
              />
            </div>
            <div className="attendance-filter-item attendance-filter-item--action">
              <AppButton
                variant="ghost"
                size="sm"
                onClick={() => setFilters({ branchId: "", startDate: "", endDate: "" })}
              >
                Clear
              </AppButton>
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* Overview Stats */}
      <div className="attendance-analytics-stats">
        <StatsCard
          title="Total Sessions"
          value={overview.totalSessions || 0}
          icon={Calendar}
          color="blue"
        />
        <StatsCard
          title="Total Attendees"
          value={overview.totalParticipants || 0}
          icon={Users}
          color="green"
        />
        <StatsCard
          title="Avg per Session"
          value={Math.round(overview.avgParticipants || 0)}
          icon={UserCheck}
          color="purple"
        />
        <StatsCard
          title="Avg Duration"
          value={`${Math.round(overview.avgDurationMins || 0)} min`}
          icon={Clock}
          color="orange"
        />
      </div>

      {/* Charts Row */}
      <div className="attendance-charts-grid">
        {/* Daily Trend */}
        <AppCard className="attendance-chart-card">
          <AppCardHeader>
            <AppCardTitle>
              <span className="attendance-card-title"><TrendingUp size={16} /> Daily Attendance Trend</span>
            </AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            {dailyData.length === 0 ? (
              <p className="text-center text-muted py-3">No data available</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: "var(--text-secondary)" }}
                    tickFormatter={(v) => v?.slice(5) || ""}
                  />
                  <YAxis tick={{ fontSize: 11, fill: "var(--text-secondary)" }} />
                  <Tooltip
                    contentStyle={{
                      background: "var(--card-bg)",
                      border: "1px solid var(--border-primary)",
                      borderRadius: 8,
                      color: "var(--text-primary)",
                    }}
                  />
                  <Line type="monotone" dataKey="present" stroke="#22c55e" strokeWidth={2} name="Present" />
                  <Line type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={2} name="Total" />
                  <Legend />
                </LineChart>
              </ResponsiveContainer>
            )}
          </AppCardContent>
        </AppCard>

        {/* Status Breakdown Pie */}
        <AppCard className="attendance-chart-card">
          <AppCardHeader>
            <AppCardTitle>
              <span className="attendance-card-title"><UserCheck size={16} /> Status Breakdown</span>
            </AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            {statusData.length === 0 ? (
              <p className="text-center text-muted py-3">No data available</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                    {statusData.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </AppCardContent>
        </AppCard>

        {/* Source Breakdown */}
        <AppCard className="attendance-chart-card">
          <AppCardHeader>
            <AppCardTitle>
              <span className="attendance-card-title"><Wifi size={16} /> Online vs Offline</span>
            </AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            {sourceData.length === 0 ? (
              <p className="text-center text-muted py-3">No data available</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={sourceData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                    <Cell fill="#3b82f6" />
                    <Cell fill="#f59e0b" />
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </AppCardContent>
        </AppCard>

        {/* Batch-wise Performance */}
        <AppCard className="attendance-chart-card attendance-chart-card--wide">
          <AppCardHeader>
            <AppCardTitle>
              <span className="attendance-card-title"><BarChart3 size={16} /> Batch-wise Attendance</span>
            </AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            {batchWise.length === 0 ? (
              <p className="text-center text-muted py-3">No data available</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={batchWise}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" />
                  <XAxis
                    dataKey="batchName"
                    tick={{ fontSize: 11, fill: "var(--text-secondary)" }}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tick={{ fontSize: 11, fill: "var(--text-secondary)" }} />
                  <Tooltip
                    contentStyle={{
                      background: "var(--card-bg)",
                      border: "1px solid var(--border-primary)",
                      borderRadius: 8,
                      color: "var(--text-primary)",
                    }}
                  />
                  <Bar dataKey="avgAttendancePct" fill="#3b82f6" name="Avg Attendance %" radius={[4, 4, 0, 0]} />
                  <Legend />
                </BarChart>
              </ResponsiveContainer>
            )}
          </AppCardContent>
        </AppCard>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default AttendanceAnalyticsPage;
